import { Box, Button, Divider, InputAdornment, Stack, TextField } from "@mui/material";
import { SearchIcon } from 'src/icons/SearchIcon';
import { ts } from "src/utils/translate";
import { PlusSvgIcon } from "src/pages/ProjectsPage/icons/PlusIcon";
import {
    GetProductsOfRoomsByFloorsFloor,
    GetProductsOfRoomsByFloorsProduct,
    GetProductsOfRoomsByFloorsResponse,
    GetProductsOfRoomsByFloorsRoom
} from "src/model/api/v2/data-contracts";
import React, { ChangeEvent, useMemo, useState } from "react";
import { DeleteFloorDialog } from "src/pages/PlannerPage/FloorView/dialogs/DeleteFloorDialog";
import { DeleteRoomDialog } from "src/pages/PlannerPage/FloorView/dialogs/DeleteRoomDialog";
import { EditFloorDialog } from "src/pages/PlannerPage/FloorView/dialogs/EditFloorDialog";
import { FloorAccordionView } from "src/pages/PlannerPage/FloorView/FloorAccordionView";
import { plannerUiSlice } from 'src/api/planner-ui-slice';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import {
    focusProductOnPlanner,
    focusRoomForEditing,
    highlightRoomOnPlanner,
    isPlannerInRoomEditMode,
} from 'src/pages/PlannerPage/FloorView/floorViewActions';
import { canModifyProjectByRoomId, toastNotProError } from 'src/components/Planner/controller';

interface FloorViewProps {
  projectId: number,
  productsOfRoomsByFloors: GetProductsOfRoomsByFloorsResponse,
  onAddFloor: () => void
}

function filterProductsOfRoomsByFloor(filterText: string, productsOfRoomsByFloors: GetProductsOfRoomsByFloorsResponse) {
  if (filterText === '') {
    return productsOfRoomsByFloors.floors
  } else {
    return productsOfRoomsByFloors.floors
    .map((floor: GetProductsOfRoomsByFloorsFloor) => {
      const filteredRooms: GetProductsOfRoomsByFloorsRoom[] = floor.rooms
      .map((room: GetProductsOfRoomsByFloorsRoom) => {
        const filteredProducts: GetProductsOfRoomsByFloorsProduct[] = room.products.filter(product =>
            product.name.toLowerCase().includes(filterText)
        );
        return {...room, products: filteredProducts};
      })
      .filter(room => room.products.length > 0);
      return {...floor, rooms: filteredRooms};
    })
    .filter(floor => floor.rooms.length > 0);
  }
}

export const FloorView = ({projectId, productsOfRoomsByFloors, onAddFloor}: FloorViewProps) => {
  const dispatch = useAppDispatch();
  const selectedFloorId = useAppSelector((state) => state.plannerUi.selectedFloorId);
  const selectedRoom = useAppSelector((state) => state.plannerUi.selectedRoom);
  const selectedDevice = useAppSelector((state) => state.plannerUi.selectedDevice);
  const [floorToEdit, setFloorToEdit] = useState<GetProductsOfRoomsByFloorsFloor | null>(null);
  const [floorToDelete, setFloorToDelete] = useState<GetProductsOfRoomsByFloorsFloor | null>(null);
  const [roomToDelete, setRoomToDelete] = useState<GetProductsOfRoomsByFloorsRoom | null>(null);

  const [filterText, setFilterText] = useState('');

  const filterFloors = (event: ChangeEvent<HTMLInputElement>) => {
    setFilterText(event.target.value.toLowerCase());
  };

  const filteredFloors: GetProductsOfRoomsByFloorsFloor[] = useMemo(() => filterProductsOfRoomsByFloor(filterText, productsOfRoomsByFloors), [filterText, productsOfRoomsByFloors]);

  return (
      <>
        <Stack
            direction="column"
            spacing={1.5}
            sx={{pt: 1}}
            divider={<Divider flexItem/>}
        >
            <TextField
                placeholder={ts('planner_placed_device_search', 'Search for placed devices...')}
                variant="outlined"
                onChange={filterFloors}
                sx={{
                    boxShadow: 'none',
                    '& .MuiOutlinedInput-root': {
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: 'none',
                        },
                        '&.Mui-focused': {
                            boxShadow: 'none',
                        },
                    },
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon/>
                        </InputAdornment>
                    ),
                  },
                }}
            />

          <Button variant="contained" startIcon={<PlusSvgIcon/>} onClick={onAddFloor}>
            {ts('planner_add_new_floor', 'Add New floor')}
          </Button>
          <Box>
              <FloorAccordionView
                  floors={filteredFloors}
                  selectedFloorId={selectedFloorId}
                  selectedRoom={selectedRoom}
                  selectedDevice={selectedDevice}
                  onEditFloor={(floor) => {
                      setFloorToEdit(floor);
                  }}
                  onDeleteFloor={(floor) => {
                      setFloorToDelete(floor);
                  }}
                  onEditRoom={(room) => {
                      if (isPlannerInRoomEditMode()) return;
                      if (!canModifyProjectByRoomId(room.room_id)) {
                          toastNotProError();
                          return;
                      }
                      dispatch(plannerUiSlice.actions.setSelectedRoom({ id: room.room_id }));
                      dispatch(plannerUiSlice.actions.setSelectedDevice({ productNameId: null, productId: null }));
                      focusRoomForEditing(room.room_id);
                  }}
                  onDeleteRoom={(room) => {
                      setRoomToDelete(room);
                  }}
                  onFloorExpand={(floor, isExpanded) => {
                      const selectedFloorId = isExpanded ? floor.floor_id : null;
                      dispatch(plannerUiSlice.actions.setSelectedFloorId(selectedFloorId));
                  }}
                  onRoomExpand={(room, isExpanded) => {
                      if (isPlannerInRoomEditMode()) return;
                      if (!isExpanded) return;
                      highlightRoomOnPlanner(room.room_id);
                  }}
                  onProductClick={(product) => {
                      if (isPlannerInRoomEditMode()) return;
                      dispatch(plannerUiSlice.actions.setSelectedDevice({
                          productNameId: null,
                          productId: product.product_id,
                      }));
                      focusProductOnPlanner(product);
                  }}
              />
          </Box>
        </Stack>

        {floorToEdit && (
            <EditFloorDialog projectId={projectId} floor={floorToEdit} onClose={() => setFloorToEdit(null)} />
        )}

        {floorToDelete && (
            <DeleteFloorDialog
                onClose={() => setFloorToDelete(null)}
                floor={floorToDelete}
            />
        )}

        {roomToDelete && (
          <DeleteRoomDialog
              onClose={() => setRoomToDelete(null)}
              room={roomToDelete}
          />

        )}
      </>
  )
}